import React, { FC, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Autocomplete from '@material-ui/lab/Autocomplete'

import { generalActions } from 'src/actions/GeneralActions'
import { RootState } from 'src/Store'

import { searchFieldStyle } from './SearchField.style'
import { CircularProgress, TextField } from '@material-ui/core'
import { AddressResult } from 'src/dataSources/dataforsyningen/Address'
import { mapActions } from 'src/actions/MapActions'
import { useIntl } from 'react-intl'

/**
 * SearchFieldProps contains properties for the component.
 */
export interface SearchFieldProps {} // eslint-disable-line @typescript-eslint/no-empty-interface
const defaultProps: SearchFieldProps = {
}

/**
 * SearchFieldOption represents an address option that can be selected.
 */
interface SearchFieldOption {
  label: string
  address?: AddressResult
}

/**
 * SearchField React component.
 * @param {object} props React properties object.
 * @returns React view component.
 */
export const SearchField: FC<SearchFieldProps> = (props: SearchFieldProps) => {
  const classes = searchFieldStyle()
  const dispatch = useDispatch()
  const intl = useIntl()
  const addresses = useSelector((state: RootState): AddressResult[] | undefined => state.generalState.addresses)
  const addressQuery = useSelector((state: RootState): string | undefined => state.generalState.addressQuery)
  const [open, setOpen] = React.useState(false)
  const [options, setOptions] = React.useState([] as SearchFieldOption[])
  const [updatingAddresses, setUpdatingAddresses] = React.useState<number|undefined>(undefined)

  const queryRef = useRef(addressQuery)
  queryRef.current = addressQuery

  const loading = open && options.length === 0 && addressQuery !== ''

  useEffect(() => {
    if (addresses === undefined) {
      return
    }
    const options: SearchFieldOption[] = []
    for (const address of addresses) {
      let fullAddress = ''
      if (address.data !== undefined) {
        if (address.data?.vejnavn !== undefined) {
          fullAddress += address.data.vejnavn
        }
        if (address.data?.husnr !== undefined) {
          fullAddress += ' ' + address.data.husnr
        }
        if (address.data?.supplerendebynavn !== undefined && address.data?.supplerendebynavn !== null) {
          fullAddress += ', ' + address.data.supplerendebynavn
        }
        if (address.data?.postnr !== undefined) {
          fullAddress += ', ' + address.data.postnr
        }
        if (address.data?.postnrnavn !== undefined) {
          fullAddress += ' ' + address.data.postnrnavn
        }
        if (fullAddress === '' && address.tekst !== undefined) {
          fullAddress = address.tekst
        }
      }
      const option: SearchFieldOption = { label: fullAddress, address: address }
      options.push(option)
    }
    setOptions(options)
  }, [addresses])

  /**
   * Start interval to add a delay before sending a query to service.
   */
  const startUpdateAddressesInterval = (): void => {
    const timeout = window.setTimeout(() => {
      if (queryRef.current === undefined || queryRef.current === '') {
        setUpdatingAddresses(undefined)
        return
      }
      dispatch(generalActions.fetchAddresses(queryRef.current))
      setUpdatingAddresses(undefined)
    }, 500)
    setUpdatingAddresses(timeout)
  }

  const handleChange = (event: React.ChangeEvent<{}>, value: SearchFieldOption | null, reason: string): void => {
    if (value === null) {
      return
    }
    if (value.address?.data?.x !== undefined && value.address?.data?.y !== undefined) {
      dispatch(mapActions.setMapCenter([value.address.data.x, value.address.data.y]))
      dispatch(mapActions.setMapCurrentZoom(18))
    }
  }
  const handleInputChange = (event: React.ChangeEvent<{}>, value: string, reason: string): void => {
    if (value.length > 2) {
      setOptions([])
      dispatch(generalActions.setAddressQuery(value))
      if (updatingAddresses !== undefined) {
        clearTimeout(updatingAddresses)
      }
      startUpdateAddressesInterval()
    }
  }

  return (
    <div className={classes.root}>
      <Autocomplete
        id='address'
        size='small'
        open={open}
        onOpen={() => {
          setOpen(true)
        }}
        onClose={() => {
          setOpen(false)
        }}
        getOptionSelected={(option, value) => option.label === value.label}
        getOptionLabel={(option) => option.label}
        options={options}
        loading={loading}
        loadingText={intl.formatMessage({ id: 'common.loading' })}
        onChange={handleChange}
        onInputChange={handleInputChange}
        noOptionsText={intl.formatMessage({ id: 'search.nooptions' })}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={intl.formatMessage({ id: 'search.placeholder' })}
            variant='standard'
            InputProps={{
              ...params.InputProps,
              className: classes.textField,
              disableUnderline: true,
              endAdornment: (
                <>
                  {loading ? <CircularProgress className={classes.loading} color='inherit' size={15} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </div>
  )
}

SearchField.defaultProps = defaultProps

export default SearchField
